import {DEFAULTS, Script} from '../../index.js';

/**
 * Creates Hypercube.js scenes.
 * @example
 * const scene = new Scene(name);
 */
class Scene {

    /**
     * Stores the anti-aliasing status.
     * @type {boolean}
     * @private
     */
    $antialiasing;

    /**
     * Stores the fog.
     * @type {import('../../index.js').Fog}
     * @private
     */
    $fog;

    /**
     * Stores the ambiant light.
     * @type {import('../../index.js').LightAmbiant}
     * @private
     */
    $lightambiant;

    /**
     * Stores the directional light.
     * @type {import('../../index.js').LightDirectional}
     * @private
     */
    $lightdirectional;

    /**
     * Stores the name.
     * @type {string}
     * @private
     */
    $name;

    /**
     * Stores the camera node.
     * @type {import('../../index.js').Node}
     * @private
     */
    $nodecamera;

    /**
     * Stores the root node.
     * @type {import('../../index.js').Node}
     * @private
     */
    $noderoot;

    /**
     * Stores the skybox.
     * @type {import('../../index.js').Skybox}
     * @private
     */
    $skybox;

    /**
     * Gets the anti-aliasing status.
     * @type {boolean}
     * @public
     * @readonly
     */
    get antialiasing() {

        return this.$antialiasing;
    }

    /**
     * Gets the fog.
     * @type {import('../../index.js').Fog}
     * @public
     * @readonly
     */
    get fog() {

        return this.$fog;
    }

    /**
     * Gets the ambiant light.
     * @type {import('../../index.js').LightAmbiant}
     * @public
     * @readonly
     */
    get lightambiant() {

        return this.$lightambiant;
    }

    /**
     * Gets the directional light.
     * @type {import('../../index.js').LightDirectional}
     * @public
     * @readonly
     */
    get lightdirectional() {

        return this.$lightdirectional;
    }

    /**
     * Gets the name.
     * @type {string}
     * @public
     * @readonly
     */
    get name() {

        return this.$name;
    }

    /**
     * Gets the camera node.
     * @type {import('../../index.js').Node}
     * @public
     * @readonly
     */
    get nodecamera() {

        return this.$nodecamera;
    }

    /**
     * Gets the root node.
     * @type {import('../../index.js').Node}
     * @public
     * @readonly
     */
    get noderoot() {

        return this.$noderoot;
    }

    /**
     * Gets the skybox.
     * @type {import('../../index.js').Skybox}
     * @public
     * @readonly
     */
    get skybox() {

        return this.$skybox;
    }

    /**
     * Creates a new Hypercube.js scene.
     * @param {string} [$name] The name of the scene.
     */
    constructor($name = 'Scene') {

        this.$name = $name;

        this.$antialiasing = true;
        this.$fog = DEFAULTS.foginvisible();
        this.$lightambiant = DEFAULTS.lightambiant();
        this.$lightdirectional = DEFAULTS.lightdirectional();
        this.$noderoot = DEFAULTS.noderoot();
        this.$nodecamera = DEFAULTS.nodecamera();
        this.$skybox = DEFAULTS.skybox();
    }

    /**
     * Gets a flat array of all the nodes in the hierarchical tree of given node.
     * @param {import('../../index.js').Node} $node The reference node.
     * @returns {import('../../index.js').Node[]}
     * @public
     */
    getnodes($node) {

        const nodes = [$node];

        $node.nodes.forEach(($nodechild) => {

            nodes.push(...this.getnodes($nodechild));
        });

        return nodes;
    }

    // @TODO
    getnodesinradius($node, radius) {

        //
    }

    /**
     * Gets a flat array of all the nodes with an audio element in the hierarchical tree of given node.
     * @param {import('../../index.js').Node} $node The reference node.
     * @return {import('../../index.js').Node[]}
     * @public
     */
    getnodeswithaudio($node) {

        const nodes = [];

        if ($node.audio instanceof HTMLAudioElement) {

            nodes.push($node);
        }

        $node.nodes.forEach(($nodechild) => {

            nodes.push(...this.getnodeswithaudio($nodechild));
        });

        return nodes;
    }

    /**
     * Gets a flat array of all the nodes with given component name in the hierarchical tree of given node.
     * @param {import('../../index.js').Node} $node The reference node.
     * @param {string} $component The name of the component.
     * @return {import('../../index.js').Node[]}
     * @public
     */
    getnodeswithcomponent($node, $component) {

        const nodes = [];

        if ($node.hascomponent($component) === true) {

            nodes.push($node);
        }

        $node.nodes.forEach(($nodechild) => {

            nodes.push(...this.getnodeswithcomponent($nodechild, $component));
        });

        return nodes;
    }

    /**
     * Gets a flat array of all the nodes with meshes in the hierarchical tree of given node.
     * @param {import('../../index.js').Node} $node The reference node.
     * @return {import('../../index.js').Node[]}
     * @public
     */
    getnodeswithmesh($node) {

        const meshes = [];

        if ($node.meshes.length > 0) {

            meshes.push($node);
        }

        $node.nodes.forEach(($nodechild) => {

            meshes.push(...this.getnodeswithmesh($nodechild));
        });

        return meshes;
    }

    /**
     * Gets a flat array of all the nodes with a script in the hierarchical tree of given node.
     * @param {import('../../index.js').Node} $node The reference node.
     * @return {import('../../index.js').Node[]}
     * @public
     */
    getnodeswithscript($node) {

        const nodes = [];

        if ($node.script instanceof Script) {

            nodes.push($node);
        }

        $node.nodes.forEach(($nodechild) => {

            nodes.push(...this.getnodeswithscript($nodechild));
        });

        return nodes;
    }

    /**
     * Sets the anti-aliasing status.
     * @param {boolean} $antialiasing The anti-aliasing status to set.
     * @public
     */
    setantialiasing($antialiasing) {

        this.$antialiasing = $antialiasing;
    }

    /**
     * Sets the fog.
     * @param {import('../../index.js').Fog} $fog The fog to set.
     * @public
     */
    setfog($fog) {

        this.$fog = $fog;
    }

    /**
     * Sets the ambiant light.
     * @param {import('../../index.js').LightAmbiant} $lightambiant The ambiant light to set.
     * @public
     */
    setlightambiant($lightambiant) {

        this.$lightambiant = $lightambiant;
    }

    /**
     * Sets the directional light.
     * @param {import('../../index.js').LightDirectional} $lightdirectional The directional light to set.
     * @public
     */
    setlightdirectional($lightdirectional) {

        this.$lightdirectional = $lightdirectional;
    }

    /**
     * Sets the camera node.
     * @param {import('../../index.js').Node} $nodecamera The camera node to set.
     * @public
     */
    setnodecamera($nodecamera) {

        this.$nodecamera = $nodecamera;
    }

    /**
     * Sets the root node.
     * @param {import('../../index.js').Node} $noderoot The root node to set.
     * @public
     */
    setnoderoot($noderoot) {

        this.$noderoot = $noderoot;
    }

    /**
     * Sets the skybox.
     * @param {import('../../index.js').Skybox} $skybox The skybox to set.
     * @public
     */
    setskybox($skybox) {

        this.$skybox = $skybox;
    }
}

export {

    Scene
};

export default Scene;
